import type { CSSProperties } from 'react';
import type { Ctx } from '../ctx';
import { ago, problemCount, todayCount } from '../model';
import { DialChips, EnvChip, RegistrationChip, StateLine, Switch, VerdictChip } from '../ui';
import type { CalendlyRow, TtsRow, WebhookEntry } from '../types';

const LBL: CSSProperties = {
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '.06em',
};

export default function Overview({ ctx }: { ctx: Ctx }) {
  const { data, env, ours, entries, pipelineFor, verdict, latest, admin } = ctx;
  const dials = data.dials;
  const baseUrl = data.webhooks?.baseUrl ?? '';
  const secretsSet = data.secrets.filter((s) => s.set).length;
  const external = entries.filter((e) => e.owner !== 'ours' || !e.pipeline);

  const rowsFor = (entry: WebhookEntry): Array<TtsRow | CalendlyRow> =>
    entry.pipeline === 'tts' ? data.tts : entry.pipeline === 'calendly' ? data.calendly : [];

  return (
    <>
      <h1>
        Overview <EnvChip env={env} />
      </h1>
      <p className="sub">
        {baseUrl.replace('https://', '')} · env {env} · image <code>{data.service?.imageTag ?? '—'}</code>
      </p>
      <StateLine loading={data.loading} error={data.error} />

      <section className="grid g2">
        {ours.map((w) => {
          const p = pipelineFor(w);
          if (!p) return null;
          const v = verdict(w);
          const dial = w.pipeline ? dials?.[w.pipeline] : undefined;
          const rs = rowsFor(w);
          const last = rs.length ? ('first_seen' in rs[0] ? rs[0].first_seen : rs[0].created_at) : null;
          return (
            <div className="card pad" key={w.id} style={p.parked ? { opacity: 0.85 } : undefined}>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span>
                  <strong>{p.name}</strong> <span className="dim">· {w.source}</span>
                </span>
                <span className="row" style={{ gap: 6 }}>
                  <RegistrationChip entry={w} />
                  <VerdictChip v={v} />
                </span>
              </div>
              <div className="row" style={{ margin: '8px 0 10px', gap: 6 }}>
                <DialChips dial={dial} parked={p.parked} />
                <code className="dim">{w.registered === true ? w.id : p.route}</code>
              </div>
              <div className="grid" style={{ gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
                <div className="well">
                  <div className="lbl dim" style={LBL}>
                    Last received
                  </div>
                  <div style={{ fontWeight: 600 }}>{last ? ago(last) : '—'}</div>
                </div>
                <div className="well">
                  <div className="lbl dim" style={LBL}>
                    Today
                  </div>
                  <div className="tab" style={{ fontWeight: 600 }}>
                    {todayCount(rs)}
                  </div>
                </div>
                <div className="well">
                  <div className="lbl dim" style={LBL}>
                    Problems
                  </div>
                  <div
                    className="tab"
                    style={{
                      fontWeight: 600,
                      color: problemCount(rs) ? 'var(--warn)' : undefined,
                    }}
                  >
                    {problemCount(rs)}
                  </div>
                </div>
              </div>
              <div className="row" style={{ marginTop: 10, gap: 5 }}>
                {p.checks.map((c) => {
                  const e = latest.find((x) => x.check === c);
                  const vv = v === 'off' ? 'off' : (e?.verdict ?? 'green');
                  const cls =
                    vv === 'green' ? 'ok' : vv === 'amber' ? 'warn' : vv === 'red' ? 'bad' : '';
                  return (
                    <span className={`chip mono ${cls}`} key={c}>
                      {c}
                    </span>
                  );
                })}
              </div>
              {p.parked ? (
                <p className="note" style={{ margin: '10px 0 0' }}>
                  Owned by OptiSales; dial off, events recorded as <code>skipped:disabled</code>.
                </p>
              ) : null}
              <div className="row" style={{ marginTop: 12 }}>
                {(['pipeline', 'monitor', 'logs', 'settings'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    className="btn sm"
                    onClick={() => ctx.go({ view: 'webhook', id: w.id, tab: t })}
                  >
                    {t[0].toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
        {!ours.length && !data.loading ? (
          <div className="empty">No webhook of ours is registered on this env.</div>
        ) : null}
      </section>

      <section className="grid g2">
        <div className="card pad">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <strong>Service</strong>
            {dials?.monitor.enabled ? (
              <span className="chip ok">monitor on</span>
            ) : (
              <span className="chip">monitor off</span>
            )}
          </div>
          <dl className="kv" style={{ marginTop: 10 }}>
            <dt>Monitor</dt>
            <dd>
              <Switch
                on={!!dials?.monitor.enabled}
                disabled={!admin || !dials}
                title={admin ? undefined : 'admin only'}
                label="monitor enabled"
                onToggle={() => ctx.requestDial('monitor', 'enabled', !dials?.monitor.enabled)}
              />{' '}
              <span className="note">5-min tick · Slack on red transitions</span>
            </dd>
            <dt>Slack</dt>
            <dd>
              <code>{data.service?.slackChannel ?? '—'}</code>
            </dd>
            <dt>Health</dt>
            <dd>
              <code>/healthz</code>{' '}
              {data.health?.ok ? (
                <span className="chip ok">ok</span>
              ) : (
                <span className="chip bad">unreachable</span>
              )}
              {data.health?.draining ? <span className="chip warn">draining</span> : null}
            </dd>
            <dt>Secrets</dt>
            <dd>
              {secretsSet}/{data.secrets.length} keys set{' '}
              <a
                href="#/tools"
                onClick={(e) => {
                  e.preventDefault();
                  ctx.go({ view: 'tools' });
                }}
              >
                details
              </a>
            </dd>
            <dt>Image</dt>
            <dd>
              <code>{data.service?.imageTag ?? '—'}</code> · <code>{env}</code>
            </dd>
          </dl>
        </div>

        <div className="card pad">
          <h2>External webhooks on the same sources</h2>
          <p className="note" style={{ margin: '0 0 8px' }}>
            Not ours — no pipeline here; listed so cutover decisions see the full picture. Open any
            for details.
          </p>
          {external.length ? (
            external.map((x) => (
              <div
                className="row"
                key={x.id}
                style={{
                  padding: '6px 0',
                  borderBottom: '1px solid var(--border)',
                  justifyContent: 'space-between',
                }}
              >
                <span>
                  <span className={`chip ${x.owner === 'zapier' ? 'warn' : 'info'}`}>
                    {x.source} · {x.owner === 'zapier' ? 'Zapier' : 'other'}
                  </span>{' '}
                  <code style={{ fontSize: 11.5 }}>{x.target}</code>
                </span>
                <button
                  type="button"
                  className="btn sm"
                  onClick={() => ctx.go({ view: 'webhook', id: x.id, tab: 'settings' })}
                >
                  Open
                </button>
              </div>
            ))
          ) : (
            <span className="dim">none</span>
          )}
        </div>
      </section>
    </>
  );
}
